import { ActionLink, useToast } from '@cian/ui-kit';
import * as React from 'react';

import type { TAlertButton } from '../../types/alert';

const TOAST_HIDE_DELAY = 4000;

interface IAlertProps {
  type: 'error' | 'info';
  isVisible: boolean;
  text: string;
  offset?: number;
  button?: TAlertButton;
  onClose: VoidFunction;
  onCancel?: VoidFunction;
}

export const Alert = React.memo(function Alert({
  type,
  isVisible,
  text,
  offset,
  button,
  onClose,
  onCancel,
}: IAlertProps) {
  const { open: openToast, hide: hideToast } = useToast({
    indent: offset && isVisible ? `${offset}px` : undefined,
    hideDelay: TOAST_HIDE_DELAY,
  });

  const currentToastIdRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    if (currentToastIdRef.current) {
      hideToast(currentToastIdRef.current);
      currentToastIdRef.current = undefined;
    }

    if (!isVisible) {
      return;
    }

    currentToastIdRef.current = openToast({
      type,
      text,
      autoHide: true,
      hasCloseIcon: false,
      dataTestId: 'Alert',
      rightAdornment: (
        <>
          {button && <ActionLink onClick={button.onClick}>{button.text}</ActionLink>}
          {onCancel && <ActionLink onClick={onCancel}>Отменить</ActionLink>}
        </>
      ),
    });

    const hideAlertTimer = window.setTimeout(onClose, TOAST_HIDE_DELAY);

    return () => {
      window.clearTimeout(hideAlertTimer);
      if (currentToastIdRef.current) {
        hideToast(currentToastIdRef.current);
        currentToastIdRef.current = undefined;
      }
    };
  }, [hideToast, openToast, isVisible, type, text, button, onClose, onCancel]);

  return null;
});
